import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";
import "./AcceptingSlider.css";

type AcceptingSliderProps = {
  /** Whether the doctor is currently accepting new patients. */
  value: boolean;
  /** Async handler called after a committed drag flips the value. Should persist the change. */
  onCommit: (next: boolean) => void | Promise<void>;
  /** When true, the thumb is pinned and drags are ignored (e.g. while a network call is in flight). */
  disabled?: boolean;
  /** Optional ratio of track width a drag must exceed to commit. Defaults to 0.75. */
  commitThreshold?: number;
};

/**
 * Slide-to-confirm control for the Family Doctor "Accepting new patients"
 * status. Drag-only — a plain click never toggles state. Intended to prevent
 * accidental flips on a public-facing flag.
 *
 * Visual states:
 *   - Off (not accepting): red track, white thumb with ->
 *   - On (accepting):      green track, white thumb with check mark
 */
export function AcceptingSlider({
  value,
  onCommit,
  disabled = false,
  commitThreshold = 0.75,
}: AcceptingSliderProps) {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const thumbRef = useRef<HTMLButtonElement | null>(null);

  const [thumbLeft, setThumbLeft] = useState<number>(0);
  const [dragging, setDragging] = useState<boolean>(false);
  const dragStart = useRef<{ x: number; base: number } | null>(null);

  const trackWidth = useCallback(() => {
    const track = trackRef.current;
    const thumb = thumbRef.current;
    if (!track || !thumb) return 0;
    return track.getBoundingClientRect().width - thumb.getBoundingClientRect().width - 8;
  }, []);

  /** Snap the thumb to the committed value's end. */
  const settle = useCallback(
    (committed: boolean) => {
      const w = trackWidth();
      setThumbLeft(committed ? w + 4 : 4);
    },
    [trackWidth]
  );

  // Keep thumb in sync with value changes (including external updates + window resize).
  useEffect(() => {
    if (!dragging) settle(value);
  }, [value, dragging, settle]);

  useEffect(() => {
    function onResize() {
      if (!dragging) settle(value);
    }
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [value, dragging, settle]);

  function clamp(n: number, lo: number, hi: number) {
    return Math.max(lo, Math.min(hi, n));
  }

  function onPointerDown(e: ReactPointerEvent<HTMLButtonElement>) {
    if (disabled) return;
    setDragging(true);
    dragStart.current = { x: e.clientX, base: thumbLeft };
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      // ignore — older browsers
    }
  }

  function onPointerMove(e: ReactPointerEvent<HTMLButtonElement>) {
    if (!dragging || !dragStart.current) return;
    const dx = e.clientX - dragStart.current.x;
    const w = trackWidth();
    setThumbLeft(clamp(dragStart.current.base + dx, 4, w + 4));
  }

  function onPointerUp() {
    if (!dragging) return;
    setDragging(false);
    dragStart.current = null;

    const w = trackWidth();
    const ratio = w > 0 ? (thumbLeft - 4) / w : 0;
    // Must cross the threshold in the "new" direction to flip.
    const next = value
      ? ratio < 1 - commitThreshold
        ? false
        : true
      : ratio >= commitThreshold
      ? true
      : false;

    if (next !== value) {
      void onCommit(next);
    } else {
      settle(value); // snap back
    }
  }

  return (
    <div
      ref={trackRef}
      className={[
        "fd-acc-slider",
        value ? "fd-acc-slider--on" : "fd-acc-slider--off",
        dragging ? "fd-acc-slider--dragging" : "",
        disabled ? "fd-acc-slider--disabled" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      role="switch"
      aria-checked={value}
      aria-label="Accepting new patients"
    >
      <div className="fd-acc-slider-label fd-acc-slider-label--off">
        Slide to accept
      </div>
      <div className="fd-acc-slider-label fd-acc-slider-label--on">
        Accepting new patients
      </div>
      <button
        ref={thumbRef}
        type="button"
        className="fd-acc-slider-thumb"
        style={{ left: `${thumbLeft}px`, transition: dragging ? "none" : undefined }}
        disabled={disabled}
        aria-hidden="true"
        tabIndex={-1}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <span className="fd-acc-slider-thumb-glyph fd-acc-slider-thumb-glyph--off" aria-hidden="true">→</span>
        <span className="fd-acc-slider-thumb-glyph fd-acc-slider-thumb-glyph--on" aria-hidden="true">✓</span>
      </button>
    </div>
  );
}
